/**
 * Test Web Push — Roomgrid layout (TestPageShell + RgSection).
 *
 * Sections: Account & application (incl. target groups) → Notification →
 * Custom data → Actions → Scheduling. Footer "Send test" CTA.
 */

import React, { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  getAccountsGroups,
  sendPushTestNotification,
} from "../services/testService";
import { buildWebPushPayload } from "../transforms/buildWebPushPayload";
import { DEFAULT_TIMEZONE } from "../constants";
import type { WebPushFormValues } from "../types/webPushTest";
import AccountAppPicker from "./sections/AccountAppPicker";
import SchedulingSection from "./sections/SchedulingSection";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

const createEmptyWebPushFormValues = (): WebPushFormValues => ({
  accountId: "",
  appId: "",
  groupIds: [],
  notification: {
    type: "",
    title: "",
    message: "",
    icon: "",
    badge: "",
    tag: "",
    requireInteraction: false,
    data: [{ key: "", value: "" }],
    actions: [{ action: "", title: "" }],
  },
  schedule: {
    type: "NONE",
    date: null,
    time: "",
    recurring: {
      intervalValue: 0,
      intervalUnit: "",
      expiresDate: null,
      expiresTime: "",
    },
    timezone: DEFAULT_TIMEZONE,
  },
});

export const WebPushTestPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm<WebPushFormValues>({
    defaultValues: createEmptyWebPushFormValues(),
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const accountId = useWatch({ control: methods.control, name: "accountId" });
  const appId = useWatch({ control: methods.control, name: "appId" });

  const [groups, setGroups] = useState<string[]>([]);

  const dataFields = useFieldArray({
    control: methods.control,
    name: "notification.data",
  });
  const actionFields = useFieldArray({
    control: methods.control,
    name: "notification.actions",
  });

  const onSubmit = async (values: WebPushFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      const body = buildWebPushPayload(values);
      const response = await sendPushTestNotification("webNotification", {
        ...body,
      });
      if (response.success) {
        notify("resources.test.notifications.webPushSent", {
          type: "success",
        });
        methods.reset(createEmptyWebPushFormValues());
      } else {
        notify(
          response.message ??
            translate("resources.test.notifications.webPushSendFailed"),
          { type: "error" },
        );
      }
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          "resources.test.notifications.webPushSendFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!accountId || !appId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    getAccountsGroups({ accountId, application: appId })
      .then((res) => {
        if (!cancelled) setGroups(res);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, appId]);

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey="resources.test.webPush.title"
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
          <Box sx={{ mt: 2 }}>
            <Controller
              name="groupIds"
              control={methods.control}
              rules={{
                validate: (v) =>
                  (v && v.length > 0) ||
                  translate("resources.test.validation.groupRequired"),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  select
                  fullWidth
                  required
                  label={translate("resources.test.fields.groupId")}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  SelectProps={{
                    multiple: true,
                    value: field.value ?? [],
                    onChange: field.onChange,
                    renderValue: (selected) => (selected as string[]).join(", "),
                  }}
                >
                  {groups.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.notification", {
            _: "Notification",
          })}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="notification.type"
                control={methods.control}
                rules={{
                  required: translate("resources.test.validation.typeRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={translate("resources.test.fields.type")}
                    placeholder={translate("resources.test.fields.type")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="notification.title"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.validation.titleRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={translate("resources.test.fields.title")}
                    placeholder={translate("resources.test.fields.title")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="notification.message"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.validation.messageRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={translate("resources.test.fields.message")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="notification.icon"
                control={methods.control}
                rules={{
                  required: translate("resources.test.validation.iconRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={translate("resources.test.fields.icon")}
                    placeholder={translate("resources.test.fields.icon")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="notification.badge"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.validation.badgeRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={translate("resources.test.fields.badge")}
                    placeholder={translate("resources.test.fields.badge")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="notification.tag"
                control={methods.control}
                rules={{
                  required: translate("resources.test.validation.tagRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={translate("resources.test.fields.tag")}
                    placeholder={translate("resources.test.fields.tag")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="notification.requireInteraction"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    }
                    label={translate(
                      "resources.test.fields.requireInteraction",
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.customData", {
            _: "Custom data",
          })}
        >
          <Stack spacing={{ xs: 1, sm: 1.5 }}>
            {dataFields.fields.map((f, i) => (
              <Box
                key={f.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Controller
                  name={`notification.data.${i}.key` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.dataKey")}
                    />
                  )}
                />
                <Controller
                  name={`notification.data.${i}.value` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.dataValue")}
                    />
                  )}
                />
                <IconButton
                  onClick={() => dataFields.remove(i)}
                  color="error"
                  sx={{ flexShrink: 0 }}
                  aria-label="remove data row"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="contained"
              size="small"
              onClick={() => dataFields.append({ key: "", value: "" })}
              sx={{ alignSelf: "flex-start" }}
            >
              {translate("resources.test.actions.addData")}
            </Button>
          </Stack>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.actions", {
            _: "Actions",
          })}
        >
          <Stack spacing={{ xs: 1, sm: 1.5 }}>
            {actionFields.fields.map((f, i) => (
              <Box
                key={f.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Controller
                  name={`notification.actions.${i}.action` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.action")}
                    />
                  )}
                />
                <Controller
                  name={`notification.actions.${i}.title` as const}
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={translate("resources.test.fields.title")}
                    />
                  )}
                />
                <IconButton
                  onClick={() => actionFields.remove(i)}
                  color="error"
                  sx={{ flexShrink: 0 }}
                  aria-label="remove action row"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="contained"
              size="small"
              onClick={() => actionFields.append({ action: "", title: "" })}
              sx={{ alignSelf: "flex-start" }}
            >
              {translate("resources.test.actions.addActions")}
            </Button>
          </Stack>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.scheduling", {
            _: "Scheduling",
          })}
          last
        >
          <SchedulingSection />
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default WebPushTestPage;
